<script setup lang="ts">
const { te, t } = useI18n();
const appStateStore = useAppStateStore();

const props = defineProps({
  minPrice: {
    type: Number,
    required: true,
  },
  maxPrice: {
    type: Number,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
});
const selectedMin = defineModel("selectedMin", {
  type: Number,
  required: true,
});
const selectedMax = defineModel("selectedMax", {
  type: Number,
  required: true,
});

const initMinSliderValue = () => {
  if (selectedMin.value !== props.minPrice) return selectedMin.value;
  return props.minPrice;
};
const initMaxSliderValue = () => {
  if (selectedMax.value !== props.maxPrice) return selectedMax.value;
  return props.maxPrice;
};
const sliderValues = ref([initMinSliderValue(), initMaxSliderValue()]);
const sliderMin = ref(props.minPrice);
const sliderMax = ref(props.maxPrice);

const isFilterOpen = ref(false);
const priceContainer = ref<HTMLElement | null>(null);

const getFormattedFilterKey = (key: string) => {
  if (key.includes(".")) {
    const parts = key.split(".");
    const merchantKey = `${parts[0]}.property.${parts[1]}.title`;
    if (te(merchantKey))
      return t(merchantKey).charAt(0).toUpperCase() + t(merchantKey).slice(1);
  }
  if (te(key)) return t(key).charAt(0).toUpperCase() + t(key).slice(1);
  const fallbackMerchantKey = `${appStateStore.currentMerchant}.property.${key}.title`;
  if (te(fallbackMerchantKey))
    return (
      t(fallbackMerchantKey).charAt(0).toUpperCase() +
      t(fallbackMerchantKey).slice(1)
    );
  return key.charAt(0).toUpperCase() + key.slice(1);
};

watch(sliderValues, (newVal, oldVal) => {
  if (newVal[0] !== oldVal[0]) {
    selectedMin.value = newVal[0];
  }
  if (newVal[1] !== oldVal[1]) {
    selectedMax.value = newVal[1];
  }
});

watch(priceContainer, (newVal) => {
  if (isFilterOpen.value && newVal) {
    newVal.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }
});
</script>
<template>
  <div class="flex flex-col items-center justify-between">
    <div
      class="w-full flex items-baseline justify-between border-b border-secondary-500 px-8 py-4 cursor-pointer text-neutral-200"
      @click="isFilterOpen = !isFilterOpen">
      <div class="flex items-baseline gap-2">
        <p class="text-lg">
          {{ getFormattedFilterKey(props.title) }}
        </p>
        <p class="text-sm">
          {{
            selectedMin !== 0 || selectedMax !== 0
              ? ` (${
                  selectedMin !== 0 ? roundNum(selectedMin) : roundNum(minPrice)
                } - ${
                  selectedMax !== 0 ? roundNum(selectedMax) : roundNum(maxPrice)
                })`
              : ""
          }}
        </p>
      </div>
      <NuxtIcon
        name="mdi:keyboard-arrow-down"
        class="w-7 h-7 transition-all duration-300 shrink-0"
        :class="{ 'transform rotate-180': isFilterOpen }" />
    </div>
    <transition
      enter-active-class="transition ease duration-100"
      enter-from-class="transform opacity-0 -translate-y-5"
      enter-to-class="transform opacity-100 translate-y-0"
      leave-active-class="transition ease duration-100"
      leave-from-class="transform opacity-100 translate-y-0"
      leave-to-class="transform opacity-0 -translate-y-5">
      <div
        v-if="isFilterOpen"
        ref="priceContainer"
        class="w-full flex flex-col justify-center gap-4 border-b border-secondary-500 px-8 py-10 transition-all duration-300 overflow-hidden bg-neutral-500">
        <CommonSlider
          v-model:values="sliderValues"
          v-model:min="sliderMin"
          v-model:max="sliderMax" />
      </div>
    </transition>
  </div>
</template>
